/**
 * Meeting call engine: one per browser tab. It owns the media connection (LiveKit Room) and the
 * join → lobby → room state machine, so a call survives route changes. The hub authorizes every
 * step; the media connection is only opened with a grant the server issued after admission.
 *
 * Members and guests share this engine through a small backend interface; guests never touch
 * member routes.
 */
import { ApiRequestError } from '@deskworks/client';
import type { JoinContext, JoinGrant } from '@deskworks/domain';
import { DisconnectReason, MediaDeviceFailure, Room, RoomEvent, Track, VideoPresets } from 'livekit-client';
import { savePrefs, type MediaPrefs } from './media.js';

export type EndReason =
  | 'left'
  | 'removed'
  | 'ended'
  | 'locked'
  | 'full'
  | 'declined'
  | 'not_invited'
  | 'guests_not_allowed'
  | 'elsewhere'
  | 'disconnected'
  | 'error';

export interface MeetingBackend {
  readonly kind: 'member' | 'guest';
  /** First entry. May place the person in the lobby. */
  join(): Promise<JoinContext>;
  /** Current state; includes a fresh media grant when admitted. */
  status(): Promise<JoinContext>;
  /** A fresh grant after a dropped connection, without creating a new request. */
  rejoin(): Promise<JoinContext>;
  /** Lightweight access check while in the room ('ok' or why access ended). */
  check(): Promise<{ result: 'ok' | EndReason; hostPresent?: boolean; handRaised?: boolean }>;
  leave(): Promise<void>;
  /** Best-effort leave while the page is closing. */
  leaveOnUnload?(): void;
  raiseHand(raised: boolean): Promise<void>;
  /** Explains why access ended when the server no longer recognizes this person. */
  explainLostAccess?(): Promise<EndReason>;
}

export type CallPhase = 'idle' | 'joining' | 'lobby' | 'connecting' | 'connected' | 'reconnecting' | 'ended';

export interface CallState {
  phase: CallPhase;
  kind: 'member' | 'guest';
  occurrenceId: string | null;
  title: string;
  context: JoinContext | null;
  hostPresent: boolean;
  /** Media connection; null when audio/video aren't available (chat and people still work). */
  room: Room | null;
  mediaUnavailable: string | null;
  canModerate: boolean;
  joinedAt: number | null;
  end: EndReason | null;
  endDetail: string | null;
  mic: boolean;
  cam: boolean;
  share: boolean;
  handRaised: boolean;
  deviceError: string | null;
  audioBlocked: boolean;
  prefs: MediaPrefs;
}

const INITIAL: CallState = {
  phase: 'idle',
  kind: 'member',
  occurrenceId: null,
  title: '',
  context: null,
  hostPresent: false,
  room: null,
  mediaUnavailable: null,
  canModerate: false,
  joinedAt: null,
  end: null,
  endDetail: null,
  mic: false,
  cam: false,
  share: false,
  handRaised: false,
  deviceError: null,
  audioBlocked: false,
  prefs: { micOn: true, camOn: true },
};

export const END_COPY: Record<EndReason, { title: string; body: string; retry?: string }> = {
  left: { title: 'You left the meeting', body: 'You can rejoin while the meeting is still going.', retry: 'Rejoin' },
  removed: { title: 'You were removed from this meeting', body: 'The host removed you from the meeting, so you can’t rejoin it. If you think this is a mistake, contact the organizer.' },
  ended: { title: 'The meeting has ended', body: 'The host ended the meeting for everyone.' },
  locked: { title: 'This meeting is locked', body: 'The host has stopped new people from joining. Ask the host to unlock it, then try again.', retry: 'Try again' },
  full: { title: 'This meeting is full', body: 'It has reached the maximum number of people. Try again later or contact the organizer.', retry: 'Try again' },
  declined: { title: 'You weren’t let in', body: 'The host declined your request to join this meeting.' },
  not_invited: { title: 'You don’t have access to this meeting', body: 'Ask the organizer to invite you or to send you the meeting link.' },
  guests_not_allowed: { title: 'Guests can’t join this meeting', body: 'The organizer hasn’t allowed guests in this meeting.' },
  elsewhere: { title: 'You joined from another window', body: 'This meeting is now open in another window or on another device.', retry: 'Join here' },
  disconnected: { title: 'Your connection to the meeting was lost', body: 'We couldn’t reconnect. Check your internet connection, then rejoin.', retry: 'Rejoin' },
  error: { title: 'Couldn’t join the meeting', body: 'Something went wrong while joining.', retry: 'Try again' },
};

const LOBBY_POLL_MS = 4000;
/** Quick resume attempts by the media connection itself (~13s) before rejoining with a fresh grant. */
const RESUME_DELAYS_MS = [0, 300, 1200, 2700, 4000, 5000];
/** If a resume hasn't finished by then (it can stall), drop it and rejoin with a fresh grant. */
const RESUME_TIMEOUT_MS = 15_000;
const WATCH_MS = 20_000;
const MAX_REJOIN_ATTEMPTS = 6;

function accessEnded(err: unknown) {
  return err instanceof ApiRequestError && (err.status === 401 || err.status === 403 || err.status === 404);
}

function deviceFailureMessage(err: unknown, kind: 'camera' | 'microphone' | 'screen') {
  if (kind === 'screen') return 'Screen sharing couldn’t start.';
  const f = MediaDeviceFailure.getFailure(err);
  if (f === MediaDeviceFailure.PermissionDenied) return `${kind === 'camera' ? 'Camera' : 'Microphone'} access is blocked for this site. Allow it in your browser's site settings.`;
  if (f === MediaDeviceFailure.NotFound) return `No ${kind} was found.`;
  if (f === MediaDeviceFailure.DeviceInUse) return `Your ${kind} is being used by another app.`;
  return `Your ${kind} couldn’t start.`;
}

export class MeetingCall {
  private state: CallState = INITIAL;
  private listeners = new Set<() => void>();
  private announcers = new Set<(msg: string, important?: boolean) => void>();
  private backend: MeetingBackend | null = null;
  private room: Room | null = null;
  private generation = 0;
  private lobbyTimer: ReturnType<typeof setInterval> | null = null;
  private watchTimer: ReturnType<typeof setInterval> | null = null;
  private rejoinTimer: ReturnType<typeof setTimeout> | null = null;
  private resumeWatchdog: ReturnType<typeof setTimeout> | null = null;
  private rejoinAttempt = 0;
  private wasInLobby = false;
  private checking = false;
  private lastCheck = 0;
  private expectingMicChange = false;
  private onlineHandler = () => this.retryNow();

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  };

  getSnapshot = () => this.state;

  /** Screen-reader announcements and in-app notices for call events. */
  onAnnounce(fn: (msg: string, important?: boolean) => void) {
    this.announcers.add(fn);
    return () => void this.announcers.delete(fn);
  }

  /** Announce a call event to screen readers (and as a notice when important). */
  announce(msg: string, important = false) {
    for (const a of this.announcers) a(msg, important);
  }

  private set(patch: Partial<CallState>) {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  get active() {
    return ['joining', 'lobby', 'connecting', 'connected', 'reconnecting'].includes(this.state.phase);
  }

  /* ------------------------------------------------------------------ join */

  async start(backend: MeetingBackend, prefs: MediaPrefs, title = '') {
    if (this.active) await this.leave('idle');
    const gen = ++this.generation;
    this.backend = backend;
    this.wasInLobby = false;
    this.rejoinAttempt = 0;
    savePrefs(prefs);
    this.set({ ...INITIAL, phase: 'joining', kind: backend.kind, title, prefs, mic: false, cam: false });
    window.addEventListener('online', this.onlineHandler);
    try {
      const ctx = await backend.join();
      if (gen !== this.generation) return;
      await this.apply(ctx, gen);
    } catch (err) {
      if (gen !== this.generation) return;
      if (err instanceof ApiRequestError && err.status === 404) this.finish('not_invited', err.message);
      else if (err instanceof ApiRequestError && err.status === 403) this.finish(/locked/i.test(err.message) ? 'locked' : 'guests_not_allowed', err.message);
      else this.finish('error', (err as Error).message);
    }
  }

  /** Start again with the previous backend (Rejoin / Try again). */
  async restart() {
    if (!this.backend) return;
    await this.start(this.backend, this.state.prefs, this.state.title);
  }

  private async apply(ctx: JoinContext, gen: number) {
    this.set({ context: ctx, occurrenceId: ctx.occurrence.id, title: ctx.meeting.title, handRaised: ctx.me?.handRaised ?? this.state.handRaised });
    const r = ctx.result;
    if (r.status === 'denied') {
      const reason: EndReason = r.reason === 'not_invited' && this.wasInLobby ? 'declined' : r.reason;
      this.finish(reason);
      return;
    }
    if (r.status === 'lobby') {
      const first = this.state.phase !== 'lobby';
      this.wasInLobby = true;
      this.set({ phase: 'lobby', hostPresent: r.hostPresent });
      if (first) this.announce(r.hostPresent ? 'Waiting for the host to let you in.' : 'The host hasn’t joined yet. You’ll join when the host lets you in.');
      this.startLobbyPolling(gen);
      return;
    }
    this.stopLobbyPolling();
    if (this.wasInLobby && !this.state.joinedAt) this.announce('The host let you in.', true);
    const canModerate = ctx.me?.role === 'host' || ctx.me?.role === 'cohost';
    if (!r.grant) {
      this.set({
        phase: 'connected',
        canModerate,
        mediaUnavailable: r.mediaUnavailableReason ?? 'Audio and video aren’t available right now. Meeting chat still works.',
        joinedAt: this.state.joinedAt ?? Date.now(),
      });
      this.startWatch(gen);
      return;
    }
    await this.connectMedia(r.grant, gen, canModerate);
  }

  private async connectMedia(grant: JoinGrant, gen: number, canModerate: boolean) {
    const reconnect = this.state.phase === 'reconnecting';
    this.set({ phase: reconnect ? 'reconnecting' : 'connecting', canModerate: canModerate || grant.canModerate });
    const { prefs } = this.state;
    const room = new Room({
      adaptiveStream: true,
      dynacast: true,
      audioCaptureDefaults: { deviceId: prefs.audioInput, echoCancellation: true, noiseSuppression: true },
      videoCaptureDefaults: { deviceId: prefs.videoInput, resolution: VideoPresets.h720.resolution },
      audioOutput: prefs.audioOutput ? { deviceId: prefs.audioOutput } : undefined,
      publishDefaults: { simulcast: true },
      reconnectPolicy: { nextRetryDelayInMs: (c) => RESUME_DELAYS_MS[c.retryCount] ?? null },
    });
    this.wire(room, gen);
    try {
      await room.connect(grant.serverUrl, grant.token);
    } catch (err) {
      room.removeAllListeners();
      void room.disconnect();
      if (gen !== this.generation) return;
      this.scheduleRejoin(gen, (err as Error).message);
      return;
    }
    if (gen !== this.generation) {
      room.removeAllListeners();
      void room.disconnect();
      return;
    }
    this.room = room;
    this.rejoinAttempt = 0;
    const first = !this.state.joinedAt;
    this.set({ room, phase: 'connected', mediaUnavailable: null, joinedAt: this.state.joinedAt ?? Date.now(), audioBlocked: !room.canPlaybackAudio });
    this.announce(first ? 'You’re in the meeting.' : 'Reconnected.', !first);
    this.startWatch(gen);
    // Turn on what the person chose before joining (or had on before a reconnect).
    const wantMic = reconnect ? this.state.mic || prefs.micOn : prefs.micOn;
    const wantCam = reconnect ? this.state.cam || prefs.camOn : prefs.camOn;
    if (wantMic) await this.setMic(true);
    if (wantCam) await this.setCam(true);
  }

  private wire(room: Room, gen: number) {
    const sync = () => {
      if (room !== this.room) return;
      const lp = room.localParticipant;
      this.set({ mic: lp.isMicrophoneEnabled, cam: lp.isCameraEnabled, share: lp.isScreenShareEnabled });
    };
    const reconnecting = () => {
      if (room !== this.room || this.state.phase === 'reconnecting') return;
      this.set({ phase: 'reconnecting' });
      this.announce('Connection lost. Reconnecting…', true);
      this.clearWatchdog();
      this.resumeWatchdog = setTimeout(() => {
        this.resumeWatchdog = null;
        if (room !== this.room || this.state.phase !== 'reconnecting' || gen !== this.generation) return;
        this.room = null;
        room.removeAllListeners();
        void room.disconnect(true);
        this.set({ room: null, mic: false, cam: false, share: false });
        this.scheduleRejoin(gen, 'resume timed out');
      }, RESUME_TIMEOUT_MS);
    };
    room
      .on(RoomEvent.Reconnecting, reconnecting)
      .on(RoomEvent.SignalReconnecting, reconnecting)
      .on(RoomEvent.Reconnected, () => {
        this.clearWatchdog();
        if (room !== this.room) return;
        this.set({ phase: 'connected' });
        this.announce('Reconnected.', true);
        void this.check(gen, true);
      })
      .on(RoomEvent.Disconnected, (reason?: DisconnectReason) => void this.onDisconnected(room, gen, reason))
      .on(RoomEvent.LocalTrackPublished, sync)
      .on(RoomEvent.LocalTrackUnpublished, sync)
      .on(RoomEvent.TrackMuted, (pub, participant) => {
        sync();
        if (participant === room.localParticipant && pub.source === Track.Source.Microphone && !this.expectingMicChange) {
          // Stay muted after a reconnect too, until the person turns the microphone back on.
          this.set({ prefs: { ...this.state.prefs, micOn: false } });
          this.announce('The host muted your microphone.', true);
        }
      })
      .on(RoomEvent.TrackUnmuted, sync)
      .on(RoomEvent.AudioPlaybackStatusChanged, () => this.set({ audioBlocked: !room.canPlaybackAudio }))
      .on(RoomEvent.MediaDevicesError, (err: Error) => this.set({ deviceError: deviceFailureMessage(err, 'microphone') }));
  }

  private clearWatchdog() {
    if (this.resumeWatchdog) clearTimeout(this.resumeWatchdog);
    this.resumeWatchdog = null;
  }

  private async onDisconnected(room: Room, gen: number, reason?: DisconnectReason) {
    if (gen !== this.generation || room !== this.room) return;
    this.clearWatchdog();
    this.room = null;
    room.removeAllListeners();
    this.set({ room: null, mic: false, cam: false, share: false });
    if (reason === DisconnectReason.CLIENT_INITIATED) return;
    if (reason === DisconnectReason.DUPLICATE_IDENTITY) {
      this.finish('elsewhere');
      return;
    }
    if (reason === DisconnectReason.PARTICIPANT_REMOVED) {
      await this.verifyAfterEviction(gen, 'removed');
      return;
    }
    if (reason === DisconnectReason.ROOM_DELETED || reason === DisconnectReason.ROOM_CLOSED) {
      await this.verifyAfterEviction(gen, 'ended');
      return;
    }
    this.scheduleRejoin(gen);
  }

  /** The media server closed our connection: confirm with the hub why, and never auto-rejoin a removal. */
  private async verifyAfterEviction(gen: number, fallback: EndReason) {
    try {
      const c = await this.backend!.check();
      if (gen !== this.generation) return;
      if (c.result !== 'ok') this.finish(c.result);
      else if (fallback === 'ended') this.scheduleRejoin(gen);
      else this.finish(fallback);
    } catch (err) {
      if (gen !== this.generation) return;
      this.finish(accessEnded(err) ? await this.explain(fallback) : fallback);
    }
  }

  private async explain(fallback: EndReason): Promise<EndReason> {
    try {
      return (await this.backend?.explainLostAccess?.()) ?? fallback;
    } catch {
      return fallback;
    }
  }

  private scheduleRejoin(gen: number, detail?: string) {
    if (gen !== this.generation) return;
    const attempt = ++this.rejoinAttempt;
    if (attempt > MAX_REJOIN_ATTEMPTS) {
      this.finish('disconnected', detail);
      void this.backend?.leave().catch(() => undefined);
      return;
    }
    if (this.state.phase !== 'reconnecting') {
      this.set({ phase: 'reconnecting' });
      this.announce('Connection lost. Reconnecting…', true);
    }
    const delay = attempt === 1 ? 300 : Math.min(15_000, 1000 * 2 ** (attempt - 1));
    if (this.rejoinTimer) clearTimeout(this.rejoinTimer);
    this.rejoinTimer = setTimeout(() => void this.rejoinNow(gen), delay);
  }

  private async rejoinNow(gen: number) {
    this.rejoinTimer = null;
    if (gen !== this.generation || !this.backend) return;
    try {
      const ctx = await this.backend.rejoin();
      if (gen !== this.generation) return;
      await this.apply(ctx, gen);
    } catch (err) {
      if (gen !== this.generation) return;
      if (accessEnded(err)) this.finish(await this.explain('removed'));
      else this.scheduleRejoin(gen, (err as Error).message);
    }
  }

  private retryNow() {
    if (this.state.phase === 'reconnecting' && this.rejoinTimer) {
      clearTimeout(this.rejoinTimer);
      void this.rejoinNow(this.generation);
    } else if (this.state.phase === 'lobby') {
      void this.pollLobby(this.generation);
    }
  }

  /* ------------------------------------------------------------- watching */

  private startLobbyPolling(gen: number) {
    if (this.lobbyTimer) return;
    this.lobbyTimer = setInterval(() => void this.pollLobby(gen), LOBBY_POLL_MS);
  }

  private stopLobbyPolling() {
    if (this.lobbyTimer) clearInterval(this.lobbyTimer);
    this.lobbyTimer = null;
  }

  private polling = false;
  private async pollLobby(gen: number) {
    if (gen !== this.generation || this.polling || this.state.phase !== 'lobby' || !this.backend) return;
    this.polling = true;
    try {
      const ctx = await this.backend.status();
      if (gen === this.generation && this.state.phase === 'lobby') await this.apply(ctx, gen);
    } catch (err) {
      if (gen === this.generation && accessEnded(err)) this.finish(await this.explain('declined'));
    } finally {
      this.polling = false;
    }
  }

  private startWatch(gen: number) {
    if (this.watchTimer) return;
    this.watchTimer = setInterval(() => void this.check(gen), WATCH_MS);
  }

  private async check(gen: number, force = false) {
    if (gen !== this.generation || !this.backend || this.checking) return;
    if (!force && Date.now() - this.lastCheck < 1500) return;
    this.checking = true;
    this.lastCheck = Date.now();
    try {
      const c = await this.backend.check();
      if (gen !== this.generation) return;
      if (c.result !== 'ok') {
        this.finish(c.result);
        return;
      }
      const patch: Partial<CallState> = {};
      if (c.hostPresent !== undefined) patch.hostPresent = c.hostPresent;
      if (c.handRaised !== undefined) patch.handRaised = c.handRaised;
      this.set(patch);
    } catch (err) {
      if (gen === this.generation && accessEnded(err)) this.finish(await this.explain('removed'));
    } finally {
      this.checking = false;
    }
  }

  /** A realtime notice said this meeting changed (lobby decision, removal, end, hand raise…). */
  notifyChanged(occurrenceId: string) {
    if (occurrenceId !== this.state.occurrenceId) return;
    if (this.state.phase === 'lobby') void this.pollLobby(this.generation);
    else if (this.state.phase === 'connected' || this.state.phase === 'reconnecting') void this.check(this.generation);
  }

  /* ------------------------------------------------------------- controls */

  async setMic(on: boolean) {
    const room = this.room;
    if (!room) return;
    this.expectingMicChange = true;
    try {
      await room.localParticipant.setMicrophoneEnabled(on, on ? { deviceId: this.state.prefs.audioInput } : undefined);
      this.set({ deviceError: null });
    } catch (err) {
      this.set({ deviceError: deviceFailureMessage(err, 'microphone') });
    } finally {
      this.expectingMicChange = false;
      if (room === this.room) this.set({ mic: room.localParticipant.isMicrophoneEnabled });
    }
    this.rememberToggles();
  }

  async setCam(on: boolean) {
    const room = this.room;
    if (!room) return;
    try {
      await room.localParticipant.setCameraEnabled(on, on ? { deviceId: this.state.prefs.videoInput, resolution: VideoPresets.h720.resolution } : undefined);
      this.set({ deviceError: null });
    } catch (err) {
      this.set({ deviceError: deviceFailureMessage(err, 'camera') });
    } finally {
      if (room === this.room) this.set({ cam: room.localParticipant.isCameraEnabled });
    }
    this.rememberToggles();
  }

  async setShare(on: boolean) {
    const room = this.room;
    if (!room) return;
    try {
      await room.localParticipant.setScreenShareEnabled(on, on ? { audio: true, selfBrowserSurface: 'exclude', surfaceSwitching: 'include', systemAudio: 'include' } : undefined);
      if (on) this.announce('You’re sharing your screen.');
    } catch (err) {
      // Closing the browser's picker is not an error worth showing.
      if ((err as DOMException)?.name !== 'NotAllowedError' && (err as DOMException)?.name !== 'AbortError') {
        this.set({ deviceError: deviceFailureMessage(err, 'screen') });
      }
    } finally {
      if (room === this.room) this.set({ share: room.localParticipant.isScreenShareEnabled });
    }
  }

  private rememberToggles() {
    const prefs = { ...this.state.prefs, micOn: this.state.mic, camOn: this.state.cam };
    this.set({ prefs });
    savePrefs(prefs);
  }

  async switchDevice(kind: 'audioinput' | 'videoinput' | 'audiooutput', deviceId: string) {
    const key = kind === 'audioinput' ? 'audioInput' : kind === 'videoinput' ? 'videoInput' : 'audioOutput';
    const prefs = { ...this.state.prefs, [key]: deviceId };
    this.set({ prefs });
    savePrefs(prefs);
    try {
      await this.room?.switchActiveDevice(kind, deviceId);
    } catch (err) {
      this.set({ deviceError: deviceFailureMessage(err, kind === 'videoinput' ? 'camera' : 'microphone') });
    }
  }

  async raiseHand(raised: boolean) {
    if (!this.backend) return;
    const prev = this.state.handRaised;
    this.set({ handRaised: raised });
    try {
      await this.backend.raiseHand(raised);
      this.announce(raised ? 'You raised your hand.' : 'You lowered your hand.');
    } catch (err) {
      this.set({ handRaised: prev });
      throw err;
    }
  }

  /** The browser blocked sound until the person interacts with the page. */
  async enableAudio() {
    await this.room?.startAudio();
    this.set({ audioBlocked: !(this.room?.canPlaybackAudio ?? true) });
  }

  clearDeviceError() {
    this.set({ deviceError: null });
  }

  /* --------------------------------------------------------------- ending */

  private stopTimers() {
    this.stopLobbyPolling();
    this.clearWatchdog();
    if (this.watchTimer) clearInterval(this.watchTimer);
    this.watchTimer = null;
    if (this.rejoinTimer) clearTimeout(this.rejoinTimer);
    this.rejoinTimer = null;
    window.removeEventListener('online', this.onlineHandler);
  }

  private teardownRoom() {
    const room = this.room;
    this.room = null;
    if (room) {
      room.removeAllListeners();
      void room.disconnect(true);
    }
  }

  private finish(reason: EndReason, detail?: string) {
    this.generation++;
    this.stopTimers();
    this.teardownRoom();
    this.set({ phase: 'ended', end: reason, endDetail: detail ?? null, room: null, mic: false, cam: false, share: false, handRaised: false });
    if (reason !== 'left') this.announce(END_COPY[reason].title + '.', true);
  }

  /** Leave the call. 'ended' keeps a "You left" screen; 'idle' returns straight to the start. */
  async leave(then: 'ended' | 'idle' = 'ended') {
    const backend = this.backend;
    const wasActive = this.active;
    this.generation++;
    this.stopTimers();
    this.teardownRoom();
    if (then === 'idle') this.set({ ...INITIAL, prefs: this.state.prefs, kind: this.state.kind });
    else this.set({ phase: 'ended', end: 'left', endDetail: null, room: null, mic: false, cam: false, share: false, handRaised: false });
    if (wasActive && backend) await backend.leave().catch(() => undefined);
  }

  /** Back to idle after an ended screen. */
  reset() {
    if (this.active) return;
    this.set({ ...INITIAL, prefs: this.state.prefs, kind: this.state.kind });
  }

  /** The tab is closing: let the hub know so the participant list stays accurate. */
  pageHide() {
    if (!this.active) return;
    this.backend?.leaveOnUnload?.();
  }
}
